import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { listAssignmentHistory } from '../../api/leads';
import { EmptyState } from '../../components/EmptyState';
import { leadDetailState } from '../../utils/leadNavigation';

const TYPE_LABELS: Record<string, string> = {
  UNASSIGNED: 'Unassigned',
  COUNSELOR_CREATED: 'Counselor Created',
  CENTRAL_ASSIGNED: 'Central Assigned',
  TRANSFERRED: 'Transferred',
};

/** Lead Manager dashboard section (and anyone else with leads:assign) —
 * a lightweight recent-activity feed reusing the same pool-wide
 * assignment-history endpoint the Central Lead Distribution page's full
 * table already calls, rather than a second aggregate history query. */
export const RecentAssignmentsCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery({
    queryKey: ['dashboard', 'recent-assignments'],
    queryFn: () => listAssignmentHistory({ page: 1, pageSize: 5 }),
  });

  const entries = query.data?.data ?? [];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Recent assignments</Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/lead-distribution')}
        >
          View all
        </Typography>
      </Stack>
      {entries.length === 0 ? (
        <EmptyState loading={query.isLoading} message="No assignments yet" />
      ) : (
        <List dense disablePadding>
          {entries.map((entry) => (
            <ListItemButton key={entry.id} onClick={() => navigate(`/leads/${entry.leadId}`, { state: leadDetailState(location) })} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText
                // Long names/emails shorten with "…" instead of widening the card (no sideways scroll on a phone).
                sx={{ minWidth: 0 }}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
                primary={`${entry.lead.firstName} ${entry.lead.lastName}`}
                secondary={`${entry.fromUser ? `${entry.fromUser.firstName} ${entry.fromUser.lastName}` : 'Unassigned'} → ${entry.toUser ? `${entry.toUser.firstName} ${entry.toUser.lastName}` : '—'}`}
              />
              <Box>
                {entry.assignmentType && (
                  <Chip size="small" label={TYPE_LABELS[entry.assignmentType] ?? entry.assignmentType} />
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
